import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { useAuth } from '../context/AuthContext';
import { AlertCircle, ArrowLeft, Loader } from 'lucide-react';
import AudioPlayer from '../components/media-player/AudioPlayer';
import VideoPlayer from '../components/media-player/VideoPlayer';
import { authService } from '../authService'
import { AUTH_CONFIG } from '../config'

const ContentItemPage = () => {
  // We might receive either productId/itemId or productSlug/itemSlug params
  const { productId, itemId, productSlug, itemSlug } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [contentData, setContentData] = useState(null);
  const [contentUrl, setContentUrl] = useState(null);
  const [contentType, setContentType] = useState(null);
  const [contentTitle, setContentTitle] = useState('');

  useEffect(() => {
    const fetchContentDetails = async () => {
      // Check if we have the necessary user context
      if (!user) {
        setError('Informazioni mancanti per accedere al contenuto');
        setLoading(false);
        return;
      }

      try {
        
        // Determine which IDs to use - either from URL params or from sessionStorage
        let finalProductId = productId;
        let finalItemId = itemId;
        
        // If we don't have IDs in the URL, try to get them from sessionStorage
        // This happens when we're using slug-based URLs
        if (!finalProductId && !finalItemId && (productSlug || itemSlug)) {
          finalProductId = sessionStorage.getItem('currentProductId');
          finalItemId = sessionStorage.getItem('currentItemId');
          
          // If we still don't have valid IDs, we need to show an error
          if (!finalProductId || !finalItemId) {
            throw new Error('Impossibile identificare il contenuto richiesto');
          }
        }


        const token = authService.getToken();

        // Call the API to get the content URL and details
        const response = await fetch(
          `${AUTH_CONFIG.apiUrl}/content/content-download-url?email=${encodeURIComponent(user.email)}&product_id=${encodeURIComponent(finalProductId)}&item_id=${encodeURIComponent(finalItemId)}`,
          {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
        
        if (!response.ok) {
          throw new Error(`API error: ${response.status} ${response.statusText}`);
        }
        
        const data = await response.json();
        
        if (!data || !data.presigned_url) {
          throw new Error('URL di accesso al contenuto non disponibile');
        }

        // Create title from product_name and item_name as requested
        if (data.product_name && data.item_name) {
          setContentTitle(`${data.product_name} - ${data.item_name}`);
        } else if (data.item && data.item.name) {
          setContentTitle(data.item.name);
        } else {
          setContentTitle('Contenuto');
        }
        
        // Check if item details are available in the response
        if (data.item) {
          const item = data.item;
          setContentType(item.type?.toLowerCase());
          
          // Parse content if exists
          if (item.content) {
            try {
              const parsedContent = typeof item.content === 'string' 
                ? JSON.parse(item.content) 
                : item.content;
              setContentData(parsedContent);
            } catch (e) {
              console.error('Error parsing content JSON:', e);
            }
          }
        } else {
          // Try to determine type from URL if possible
          const url = data.presigned_url.toLowerCase();
          if (url.includes('.mp4') || url.includes('.webm') || url.includes('video')) {
            setContentType('video');
          } else if (url.includes('.mp3') || url.includes('.wav') || url.includes('audio')) {
            setContentType('audio');
          } else {
            setContentType('unknown');
          }
        }
        
        setContentUrl(data.presigned_url);
      } catch (error) {
        console.error('Error fetching content details:', error);
        setError(error.message || 'Errore durante il caricamento del contenuto');
      } finally {
        setLoading(false);
      }
    };

    fetchContentDetails();
  }, [user, productId, itemId, productSlug, itemSlug]);

  // Render the appropriate player based on content type
  const renderContentPlayer = () => {
    if (!contentUrl) return null;
    
    switch(contentType) {
      case 'audio':
        return <AudioPlayer audioUrl={contentUrl} title={contentTitle} />;
      case 'video':
        return <VideoPlayer videoUrl={contentUrl} title={contentTitle} />;
      default:
        return (
          <div className="flex flex-col items-center justify-center p-10 bg-gray-50 rounded-xl border border-gray-200">
            <AlertCircle size={40} className="text-yellow-500 mb-4" />
            <h3 className="text-lg font-medium text-gray-800 mb-2">Tipo di contenuto non supportato</h3>
            <p className="text-gray-600 text-center">
              Questo tipo di contenuto ({contentType || 'sconosciuto'}) non può essere visualizzato direttamente.
            </p>
          </div>
        );
    }
  };

  // Handle loading state
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-b from-green-50 to-white p-6">
        <div className="p-4 rounded-full bg-white border border-green-100 mb-5 shadow-sm">
          <Loader className="animate-spin text-green-600" size={40} />
        </div>
        <p className="text-gray-700 font-medium">Caricamento del contenuto...</p>
      </div>
    );
  }

  // Handle error state
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-6 text-center bg-gradient-to-b from-red-50 to-white">
        <div className="text-red-500 bg-white p-5 rounded-full mb-5 shadow-sm border border-red-100">
          <AlertCircle size={40} />
        </div>
        <h2 className="text-xl font-bold text-red-700 mb-3">Si è verificato un errore</h2>
        <p className="text-gray-600 mb-5 max-w-md">{error}</p>
        <div className="flex gap-4">
          <button 
            onClick={() => navigate('/contenuti')}
            className="px-6 py-3 bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200 transition-all shadow-sm hover:shadow-md flex items-center"
          >
            <ArrowLeft size={18} className="mr-2" />
            Torna ai contenuti
          </button>
          <button 
            onClick={() => window.location.reload()} 
            className="px-6 py-3 bg-red-500 text-white rounded-full hover:bg-red-600 transition-all shadow-sm hover:shadow-md"
          >
            Riprova
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Back button */}
        <button 
          onClick={() => navigate('/contenuti')}
          className="mb-6 flex items-center text-green-600 hover:text-green-700 transition-all"
        >
          <ArrowLeft size={20} className="mr-2" />
          <span className="font-medium">Torna ai tuoi contenuti</span>
        </button>
        
        {/* Content title */}
        <h1 className="text-2xl font-bold text-gray-800 mb-6">{contentTitle}</h1>
        
        {/* Content player */}
        <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-200 mb-8">
          {renderContentPlayer()}
        </div>
        
        {/* Content description if available */}
        {contentData?.description && (
          <div className="bg-green-50 p-6 rounded-xl border border-green-100 mb-8">
            <h2 className="text-lg font-medium text-gray-800 mb-3">Descrizione</h2>
            <p className="text-gray-700">{contentData.description}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ContentItemPage;